import React, { useState, useRef, useEffect } from "react";
import { createClient } from "@supabase/supabase-js";

// Supabase client
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

// Replace the STEM form with your actual link
const FORM_URLS = {
  STEM: "https://docs.google.com/forms/d/e/1FAIpQLSc_TJ0bzxnafkaQAR_zFbvRt_VoP01nTy0T2qi79xWh_EZvSw/viewform?embedded=true",
  ARTS: "https://docs.google.com/forms/d/e/...ARTS.../viewform?embedded=true",
  SOCIAL: "https://docs.google.com/forms/d/e/...SOCIAL.../viewform?embedded=true"
};

const EXAM_DURATION = 60 * 60; // 60 minutes
const INSTRUCTION_DURATION = 6; // seconds
const MAX_TAB_SWITCHES = 3;

const Interview = () => {
  const [selectedPathway, setSelectedPathway] = useState(null);
  const [student, setStudent] = useState(null);
  const [code, setCode] = useState("");
  const [loginMessage, setLoginMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(EXAM_DURATION);
  const [showInstructions, setShowInstructions] = useState(true);
  const [fadeInstructions, setFadeInstructions] = useState(false);
  const [instructionCountdown, setInstructionCountdown] = useState(INSTRUCTION_DURATION);
  const warningShownRef = useRef({ fifteen: false, five: false, one: false });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const tabSwitchCountRef = useRef(0);

  // --- Fullscreen ---
  const openFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) elem.requestFullscreen();
    else if (elem.mozRequestFullScreen) elem.mozRequestFullScreen();
    else if (elem.webkitRequestFullscreen) elem.webkitRequestFullscreen();
    else if (elem.msRequestFullscreen) elem.msRequestFullscreen();
  };

  const checkFullscreen = () => {
    const fsElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;
    setIsFullscreen(!!fsElement);
  };

  useEffect(() => {
    document.addEventListener("fullscreenchange", checkFullscreen);
    document.addEventListener("webkitfullscreenchange", checkFullscreen);
    document.addEventListener("mozfullscreenchange", checkFullscreen);
    document.addEventListener("MSFullscreenChange", checkFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", checkFullscreen);
      document.removeEventListener("webkitfullscreenchange", checkFullscreen);
      document.removeEventListener("mozfullscreenchange", checkFullscreen);
      document.removeEventListener("MSFullscreenChange", checkFullscreen);
    };
  }, []);

  // --- Timer ---
  useEffect(() => {
    if (!isModalOpen) return;
    const startTime = parseInt(localStorage.getItem("examStartTime")) || Date.now();
    localStorage.setItem("examStartTime", startTime);

    const timer = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      const remaining = EXAM_DURATION - elapsed;
      setTimeLeft(remaining > 0 ? remaining : 0);

      if (remaining <= 0) {
        clearInterval(timer);
        alert("⏰ Time is up! Your exam session has ended.");
        setIsModalOpen(false);
        localStorage.removeItem("examStartTime");
      }

      if (remaining <= 15 * 60 && !warningShownRef.current.fifteen) {
        alert("⚠️ 15 minutes remaining!");
        warningShownRef.current.fifteen = true;
      }
      if (remaining <= 5 * 60 && !warningShownRef.current.five) {
        alert("⚠️ Only 5 minutes remaining!");
        warningShownRef.current.five = true;
      }
      if (remaining <= 60 && !warningShownRef.current.one) {
        alert("⏳ 1 minute remaining!");
        warningShownRef.current.one = true;
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isModalOpen]);

  // --- Instructions countdown ---
  useEffect(() => {
    if (!isModalOpen) return;

    setShowInstructions(true);
    setFadeInstructions(false);
    setInstructionCountdown(INSTRUCTION_DURATION);

    const countdown = setInterval(() => {
      setInstructionCountdown(prev => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    const fadeTimer = setTimeout(() => setFadeInstructions(true), (INSTRUCTION_DURATION - 1) * 1000);
    const hideTimer = setTimeout(() => setShowInstructions(false), INSTRUCTION_DURATION * 1000);

    return () => {
      clearInterval(countdown);
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [isModalOpen]);

  // --- Tab switch monitoring ---
  useEffect(() => {
    if (!isModalOpen) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        tabSwitchCountRef.current += 1;

        if (tabSwitchCountRef.current <= MAX_TAB_SWITCHES) {
          alert(`⚠️ You have switched tabs ${tabSwitchCountRef.current} time(s). Stay on the exam page!`);
        } else {
          alert("❌ Maximum tab switches exceeded! The exam will end.");
          setIsModalOpen(false);
          localStorage.removeItem("examStartTime");
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isModalOpen]);

  // --- Login ---
  const handleLogin = async () => {
    if (!code.trim()) return setLoginMessage("❌ Enter your exam code");

    try {
      const { data, error } = await supabase
        .from("student_codes")
        .select("*")
        .eq("code", code.trim())
        .single();

      if (error || !data) return setLoginMessage("❌ Invalid code");

      if (data.used && process.env.REACT_APP_DEV_MODE !== "true") {
        return setLoginMessage("❌ Code already used");
      }

      if (process.env.REACT_APP_DEV_MODE !== "true") {
        await supabase.from("student_codes").update({ used: true }).eq("id", data.id);
      } else {
        console.warn(`DEV_MODE ON: Code ${code.trim()} reused without marking as used`);
      }

      setStudent(data);
      setLoginMessage("");
      setIsModalOpen(true);
      localStorage.setItem("examStartTime", Date.now());
      openFullscreen();
      tabSwitchCountRef.current = 0; // reset tab switch count
    } catch (err) {
      console.error(err);
      setLoginMessage("❌ Unexpected error, try again later");
    }
  };

  const finishExam = () => {
    if (window.confirm("Are you sure you want to finish the exam?")) {
      setIsModalOpen(false);
      localStorage.removeItem("examStartTime");
    }
  };

  const resumeFullscreen = () => openFullscreen();

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 py-16">
      <div className="container mx-auto px-6 max-w-5xl text-center">
        <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-6">
          Pathway Interview & Admission Assessment
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mb-12">
          Select your preferred pathway to continue.
        </p>

        <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {["STEM", "ARTS", "SOCIAL"].map(pathway => (
            <button
              key={pathway}
              onClick={() => setSelectedPathway(pathway)}
              className={`p-6 rounded-xl border text-left transition-all ${
                selectedPathway === pathway ? "border-primary bg-primary/10" : "hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <h3 className="font-bold text-lg mb-1">
                {pathway === "STEM" ? "STEM" : pathway === "ARTS" ? "Arts & Sports" : "Social Sciences"}
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {pathway === "STEM"
                  ? "Science, Technology, Engineering & Mathematics"
                  : pathway === "ARTS"
                  ? "Creative and physical disciplines"
                  : "Humanities and social studies"}
              </p>
            </button>
          ))}
        </section>
      </div>

      {/* LOGIN MODAL */}
      {selectedPathway && !student && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="relative p-8 bg-white dark:bg-slate-800 shadow-xl rounded-xl w-96 text-center transform transition-transform duration-300 scale-95 animate-fadeIn">
            <button
              onClick={() => setSelectedPathway(null)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 text-xl font-bold"
              title="Close"
            >
              ✖
            </button>

            <h2 className="mb-4 text-2xl font-bold">Enter Exam Code</h2>
            {loginMessage && <p className="mb-2 text-red-600">{loginMessage}</p>}
            <input
              type="text"
              placeholder="Enter Code"
              value={code}
              onChange={e => { setCode(e.target.value); setLoginMessage(""); }}
              className="w-full p-3 mb-4 border rounded text-center"
            />
            <button
              onClick={handleLogin}
              className="w-full py-3 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
            >
              Login
            </button>
          </div>
        </div>
      )}

      {/* EXAM MODAL */}
      {isModalOpen && student && selectedPathway && (
        <div className="fixed inset-0 z-50 w-full h-full flex flex-col bg-black/50">
          <div className="flex-1 flex flex-col w-full h-full bg-white dark:bg-slate-800 overflow-hidden">
            <div className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-700 px-4 py-2 border-b dark:border-slate-700">
              <div className="flex justify-between items-center">
                <span className="font-mono text-lg">
                  ⏱ Time Remaining: <span className="font-bold ml-2">{String(minutes).padStart(2,"0")}:{String(seconds).padStart(2,"0")}</span>
                </span>
              </div>
            </div>

            {showInstructions && (
              <div className={`flex-1 flex flex-col items-center justify-center p-6 bg-slate-50 dark:bg-slate-900 text-center text-slate-700 dark:text-slate-300 transition-opacity duration-1500 ${fadeInstructions?"opacity-0":"opacity-100"}`}>
                <div>
                  <h3 className="font-bold text-xl mb-4">Candidate Instructions</h3>
                  <ul className="list-disc list-inside space-y-2 text-left max-w-lg mx-auto">
                    <li>Duration: 60 minutes</li>
                    <li>Answer all questions carefully</li>
                    <li>One attempt only</li>
                    <li>Do not refresh or close the page</li>
                    <li>No external assistance is allowed</li>
                    <li>Do not switch tabs or applications</li>
                  </ul>
                  <p className="mt-4 italic text-sm">
                    Exam starts in {instructionCountdown} second{instructionCountdown!==1?"s":""}...
                  </p>
                </div>
              </div>
            )}

            {!showInstructions && (
              <div className="flex-1 overflow-auto h-full">
                {timeLeft > 0 ? (
                  <iframe
                    title={`${selectedPathway} Assessment Form`}
                    src={FORM_URLS[selectedPathway]}
                    className="w-full h-full"
                    frameBorder="0"
                  />
                ) : (
                  <p className="text-center text-red-500 font-bold mt-8">Time's up! You can no longer submit this exam.</p>
                )}
                <div className="flex justify-center p-4 border-t dark:border-slate-700">
                  <button
                    onClick={finishExam}
                    disabled={timeLeft <= 0}
                    className={`px-6 py-2 rounded-lg bg-primary text-white transition ${timeLeft<=0?"opacity-50 cursor-not-allowed":"hover:bg-primary-dark"}`}
                  >
                    Finish Exam
                  </button>
                </div>
              </div>
            )}

            {!isFullscreen && (
              <button
                onClick={resumeFullscreen}
                className="fixed bottom-6 right-6 z-50 px-4 py-2 bg-blue-600 text-white rounded shadow-lg hover:bg-blue-700 transition"
                title="Click to resume fullscreen"
              >
                ⬆️ Resume Fullscreen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Interview;
